import React, { useState } from "react";
import * as d3 from "d3";
import useResponsiveState from "../../../../hooks/useResponsiveState";
import "./DayRecordPanel.css";

const MOMENT_KEYS = ["One", "Two", "Three", "Four", "Five"];
const REFLECTION_KEYS = ["thankYou", "selfLess", "mindBody"];

const formatDate = (logDate) => {
  if (!logDate) return "";
  const parsed = new Date(String(logDate).slice(0, 10));
  return Number.isNaN(parsed.getTime())
    ? String(logDate)
    : d3.timeFormat("%a %d %b")(parsed);
};

const formatScore = (value) => {
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? "—" : `${parsed}`;
};

// The five slots are fixed. An unplanned moment is signal, not an omission,
// so it renders as a muted em-dash row rather than disappearing.
const readMoments = (day) =>
  MOMENT_KEYS.map((suffix) => {
    const label = day[`moment${suffix}`];
    const rawScore = day[`moment${suffix}Score`];
    const score = parseFloat(rawScore);
    const hasLabel = typeof label === "string" && label.trim() !== "";
    return {
      key: suffix,
      label: hasLabel ? label : "Not planned",
      score: Number.isNaN(score) ? null : score,
      planned: hasLabel || !Number.isNaN(score),
    };
  });

function DayRecordPanel({
  day,
  onStepDay,
  canStepBack,
  canStepForward,
  onClose,
}) {
  const { viewType } = useResponsiveState();
  const isMobile = viewType === "mobile";
  const [expandedChip, setExpandedChip] = useState(null);

  if (!day) {
    return (
      <div className="day-record-panel">
        <div className="day-record-header">
          <span className="day-record-title">Day record</span>
        </div>
        <p className="day-record-empty">
          Click a point on the chart to see that day's record.
        </p>
      </div>
    );
  }

  const moments = readMoments(day);

  const stepper = (
    <div className="day-record-stepper">
      <button
        type="button"
        onClick={() => onStepDay(-1)}
        disabled={!canStepBack}
        aria-label="Previous day"
      >
        ◀
      </button>
      <span className="day-record-date">{formatDate(day.logDate)}</span>
      <button
        type="button"
        onClick={() => onStepDay(1)}
        disabled={!canStepForward}
        aria-label="Next day"
      >
        ▶
      </button>
    </div>
  );

  // The whole entry, shared by both layouts. Mobile used to show a four-line
  // digest of it, which was the wrong trade once the sheet became a place you
  // deliberately open — the point is not to have to go to the journal page.
  const record = (
    <>
      <div className="day-record-scores">
        <div className="day-record-score-box">
          <div className="day-record-score-label">startScore</div>
          <div className="day-record-score-value">
            {formatScore(day.startScore)}
            <span className="day-record-denominator"> /5</span>
          </div>
        </div>
        <div className="day-record-score-box end">
          <div className="day-record-score-label">endScore</div>
          <div className="day-record-score-value">
            {formatScore(day.endScore)}
            <span className="day-record-denominator"> /5</span>
          </div>
        </div>
      </div>

      <div className="day-record-section-label">
        <span>Five moments</span>
        <span className="day-record-denominator">scored /10</span>
      </div>
      <div className="day-record-moments">
        {moments.map((moment) => (
          <div
            key={moment.key}
            className={`day-record-moment-row${moment.planned ? "" : " muted"}`}
          >
            <span
              className={`day-record-chip score${moment.planned ? "" : " muted"}`}
            >
              {moment.score === null ? "—" : moment.score}
            </span>
            <span className="day-record-moment-label">{moment.label}</span>
          </div>
        ))}
      </div>

      <div className="day-record-divider" />

      <div className="day-record-notes">
        {day.rememberToday && (
          <div className="day-record-note highlight">
            <div className="day-record-note-label">rememberToday</div>
            <div className="day-record-note-text">"{day.rememberToday}"</div>
          </div>
        )}
        {day.leaveBehind && (
          <div className="day-record-note">
            <div className="day-record-note-label">leaveBehind</div>
            <div className="day-record-note-text">"{day.leaveBehind}"</div>
          </div>
        )}

        <div className="day-record-tags">
          {REFLECTION_KEYS.filter((key) => day[key]).map((key) => (
            <button
              key={key}
              type="button"
              className={`day-record-chip tag${
                expandedChip === key ? " expanded" : ""
              }`}
              onClick={() =>
                setExpandedChip((prev) => (prev === key ? null : key))
              }
              aria-expanded={expandedChip === key}
            >
              {key}
            </button>
          ))}
        </div>
        {expandedChip && day[expandedChip] && (
          <p className="day-record-tag-text">{day[expandedChip]}</p>
        )}
      </div>
    </>
  );

  // Mobile is a sheet revealed by a tap on the plot: the date identifies it,
  // and the close button hands the viewport back to the chart.
  if (isMobile) {
    return (
      <div className="day-record-panel mobile">
        <div className="day-record-handle" aria-hidden="true" />
        <div className="day-record-header">
          {stepper}
          {onClose && (
            <button
              type="button"
              className="day-record-close"
              onClick={onClose}
              aria-label="Close day record"
            >
              ×
            </button>
          )}
        </div>
        {record}
      </div>
    );
  }

  return (
    <div className="day-record-panel">
      <div className="day-record-header">
        <span className="day-record-title">Day record</span>
        {stepper}
      </div>
      {record}
    </div>
  );
}

export default DayRecordPanel;
